// FILE: src/shared/lib/region-slug-dictionary.ts
// VERSION: 3.0.0
// ============================================================================
// CONTRACT:
// PURPOSE: Полный статический словарь slug↔регион для middleware и роутинга.
//          Не требует обращения к БД — безопасен для Edge Runtime middleware.
// SCOPE: [rating, middleware, sitemap, slug]
// KEYWORDS: [REGION(X): dictionary, slug, static, edge, middleware]
// LINKS: [USED_BY(region-utils.ts): slugToRegion(); USED_BY(/rating): список регионов]
// ============================================================================
// NOTE: v3.0.0 (2026-08-04): расширен с топ-15 до всех 89 субъектов РФ.
//       v2 (топ-15) ломал /rating/[region] для 74 регионов: slugToRegion = null
//       → notFound() → noindex → вылет из выдачи Яндекса.
//       Slug'ы синхронизированы с PROD seo_companies.region_slug
//       (SELECT region_slug, region, count(*) ... GROUP BY — 04.08.2026).
//       Алиасы-дубли (respublika-tatarstan и т.п.) НЕ добавлены — canonical только
//       один slug на регион, иначе дубли страниц в выдаче.
// ============================================================================

/** Slug → нормализованное имя региона */
export const REGION_SLUG_DICTIONARY: Record<string, string> = {
  // Города федерального значения
  'moskva': 'Москва',
  'sankt-peterburg': 'Санкт-Петербург',
  'sevastopol': 'Севастополь',

  // Края
  'altayskiy': 'Алтайский край',
  'zabaykalskiy': 'Забайкальский край',
  'kamchatskiy': 'Камчатский край',
  'krasnodarskiy': 'Краснодарский край',
  'krasnoyarskiy': 'Красноярский край',
  'permskiy': 'Пермский край',
  'primorskiy': 'Приморский край',
  'stavropolskiy': 'Ставропольский край',
  'khabarovskiy': 'Хабаровский край',

  // Области
  'amurskaya': 'Амурская область',
  'arkhangelskaya': 'Архангельская область',
  'astrakhanskaya': 'Астраханская область',
  'belgorodskaya': 'Белгородская область',
  'bryanskaya': 'Брянская область',
  'vladimirskaya': 'Владимирская область',
  'volgogradskaya': 'Волгоградская область',
  'vologodskaya': 'Вологодская область',
  'voronezhskaya': 'Воронежская область',
  'ivanovskaya': 'Ивановская область',
  'irkutskaya': 'Иркутская область',
  'kaliningradskaya': 'Калининградская область',
  'kaluzhskaya': 'Калужская область',
  'kemerovskaya-oblast-kuzbass': 'Кемеровская область — Кузбасс',
  'kirovskaya': 'Кировская область',
  'kostromskaya': 'Костромская область',
  'kurganskaya': 'Курганская область',
  'kurskaya': 'Курская область',
  'leningradskaya': 'Ленинградская область',
  'lipetskaya': 'Липецкая область',
  'magadanskaya': 'Магаданская область',
  'moskovskaya': 'Московская область',
  'murmanskaya': 'Мурманская область',
  'nizhegorodskaya': 'Нижегородская область',
  'novgorodskaya': 'Новгородская область',
  'novosibirskaya': 'Новосибирская область',
  'omskaya': 'Омская область',
  'orenburgskaya': 'Оренбургская область',
  'orlovskaya': 'Орловская область',
  'penzenskaya': 'Пензенская область',
  'pskovskaya': 'Псковская область',
  'rostovskaya': 'Ростовская область',
  'ryazanskaya': 'Рязанская область',
  'samarskaya': 'Самарская область',
  'saratovskaya': 'Саратовская область',
  'sakhalinskaya': 'Сахалинская область',
  'sverdlovskaya': 'Свердловская область',
  'smolenskaya': 'Смоленская область',
  'tambovskaya': 'Тамбовская область',
  'tverskaya': 'Тверская область',
  'tomskaya': 'Томская область',
  'tulskaya': 'Тульская область',
  'tyumenskaya': 'Тюменская область',
  'ulyanovskaya': 'Ульяновская область',
  'chelyabinskaya': 'Челябинская область',
  'yaroslavskaya': 'Ярославская область',

  // Республики
  'adygeya': 'Республика Адыгея',
  'altay': 'Республика Алтай',
  'bashkortostan': 'Республика Башкортостан',
  'buryatiya': 'Республика Бурятия',
  'dagestan': 'Республика Дагестан',
  'ingushetiya': 'Республика Ингушетия',
  'kabardino-balkarskaya': 'Кабардино-Балкарская Республика',
  'kalmykiya': 'Республика Калмыкия',
  'karachaevo-cherkesskaya': 'Карачаево-Черкесская Республика',
  'kareliya': 'Республика Карелия',
  'komi': 'Республика Коми',
  'krym': 'Республика Крым',
  'mariy-el': 'Республика Марий Эл',
  'mordoviya': 'Республика Мордовия',
  'sakha-yakutiya': 'Республика Саха (Якутия)',
  'severnaya-osetiya-alaniya': 'Республика Северная Осетия — Алания',
  'tatarstan': 'Республика Татарстан',
  'tyva': 'Республика Тыва',
  'udmurtskaya': 'Удмуртская Республика',
  'khakasiya': 'Республика Хакасия',
  'chechenskaya': 'Чеченская Республика',
  'chuvashiya-chuvashskaya-respublika': 'Чувашская Республика',

  // Автономные округа и область
  'evreyskaya': 'Еврейская автономная область',
  'aobl-evreyskaya': 'Еврейская автономная область',
  'nenetskiy-ao': 'Ненецкий автономный округ',
  'khanty-mansiyskiy-avtonomnyy-okrug-yugra': 'Ханты-Мансийский автономный округ — Югра',
  'chukotskiy': 'Чукотский автономный округ',
  'yamalo-nenetskiy': 'Ямало-Ненецкий автономный округ',

  // Новые регионы
  'donetskaya-narodnaya-respublika': 'Донецкая Народная Республика',
  'luganskaya-narodnaya': 'Луганская Народная Республика',
  'zaporozhskaya': 'Запорожская область',
  'khersonskaya': 'Херсонская область',
};

/** Slug'ы всех регионов — для списков и фильтров */
export const TOP_REGION_SLUGS: string[] = Object.keys(REGION_SLUG_DICTIONARY);


// NOTE: regionNameToSlug удалена — не использовалась нигде. Обратный поиск — через regionToSlug() из region-utils.ts
